import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  isDevMode,
  OnDestroy,
  signal,
} from '@angular/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { MENU_BOTTOM_ITEMS, MENU_ITEMS } from '../../../config/menu.config';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { MenuActionService } from '../../../services/menu-action.service';
import {
  LayoutFullSiderComponent,
  MenuComponent,
  MenuItem,
  MenuItemAction,
} from '@ui';
import { HeaderComponent } from '../../header/header.component';
// import { LogoComponent } from '../../logo/logo.component';
import { Subject, takeUntil } from 'rxjs';
import { PermissionsService } from '../../../services/permissions.service';
import { NavMenuHeaderComponent } from '../nav-menu-header/nav-menu-header.component';
import { APP_ITEMS, getEnvironmentSuffix } from '../../../../constants/navMenuItems.constants';
import { NavMenuItem } from '../../../../models/NavMenuItem.model';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-main-layout',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NzIconModule,
    NzLayoutModule,
    NzMenuModule,
    NzButtonModule,
    LayoutFullSiderComponent,
    MenuComponent,
    TranslocoDirective,
    HeaderComponent,
    // LogoComponent,
    NavMenuHeaderComponent,
  ],
  templateUrl: 'main-layout.component.html',
})
export class MainLayoutComponent implements OnDestroy {
  protected readonly IS_COLLAPSED_INIT: boolean = true;
  protected readonly SHOW_TEXT_INIT: boolean = true;
  protected readonly permissionsService = inject(PermissionsService);
  private destroy$ = new Subject<void>();
  protected readonly apps: NavMenuItem[] = APP_ITEMS(getEnvironmentSuffix());
  env = environment.environmentType;

  translocoService = inject(TranslocoService);
  menuActionService = inject(MenuActionService);

  menuItems = signal<MenuItem[]>([]);
  menuBottomItems = signal<MenuItem[]>([]);

  isCollapsed = signal<boolean>(this.IS_COLLAPSED_INIT);
  showText = signal<boolean>(this.SHOW_TEXT_INIT);
  permissions = this.permissionsService.permissions;

  constructor() {

    // Listen to the permissions signal changes natively
    effect(() => {
      // Accessing the signal registers it as a dependency for this effect
      const currentPermissions = this.permissions();
      this.updateLocalizeMenus();
    });
    effect(() => {
      const collapsed = this.isCollapsed();
      setTimeout(() => {
        this.showText.set(!collapsed);
      }, 200);
    });

    this.updateLocalizeMenus();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateLocalizeMenus() {
    this.translocoService
      .selectTranslation()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.menuItems.set(this.getLocalizeMenu(MENU_ITEMS));
        this.menuBottomItems.set(this.getLocalizeMenu(MENU_BOTTOM_ITEMS));
      });
  }

  toggleCollapsed(): void {
    this.isCollapsed.update((prevState) => !prevState);
  }

  handleAction(action: MenuItemAction) {
    if (action?.type) {
      this.menuActionService.executeAction(action.type, action.value);
    }
  }

  private getLocalizeMenu(menu: MenuItem[]): MenuItem[] {
    const showItem = (item: MenuItem): boolean => {
      if (isDevMode()) {
        // On localhost, show all items, including those with item.isDev true
        return true;
      }
      // On production, show items where item.isDev is false or undefined
      return item.isDev === false || item.isDev === undefined;
    };

    // Required permissions to display ADMIN
    const requiredPermissionsAdmin = ['sabt:alarm_cfg:r', 'sabt:alarm_cfg:admin', 'sabt:alarm_cfg:w'];
    const requiredPermissionsLosses = ['sabt:loss:r'];
    const userPermissions = this.permissions();

    return menu
      .filter(showItem)
      .filter((item) => {
        if (item.label === 'ADMIN.LABEL') {
            // Only show if the user has at least one of the required permissions
          return requiredPermissionsAdmin.some((perm) => userPermissions.includes(perm));
        }
        if (item.label === 'LOSSES.LABEL') {
            // Only show if the user has at least one of the required permissions
          return requiredPermissionsLosses.some((perm) => userPermissions.includes(perm));
        }
        return true;
      })
      .map((item) => ({
        ...item,
        label: this.translocoService.translate(item.label),
        items: item.items?.map((subitem) => ({
          ...subitem,
          label: this.translocoService.translate(subitem.label),
        })),
      }));
  }
}
